import React from "react";
import { Link } from "react-router-dom";

import InsightsIcon from "@mui/icons-material/Insights";

import { Button, Box, Card, CardContent, CardHeader, Typography, Stack } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import JobPlanActivitySummary from "../JobPlanActivitySummary";
import JobPlanButton from "./JobPlanButton";
import JobPlanDetails from "./JobPlanDetails";
import JPAvatar from "../JPAvatar";
import RenderDate from "../../../Common/Date/RenderDate";


function JobPlanListItem({ jobPlan, onDelete }) {
    const title = jobPlan.title || jobPlan.resourceType;
    const effectiveDate = jobPlan?.effectiveDate ?? "";
    const activityCount = jobPlan?.activities?.length ?? 0;
    const objectiveCount = jobPlan?.objectives?.length ?? 0;

    let isCompleted = jobPlan.status === "signedoff" ?? false;

    let durationUnit = "PA";
    let contractedDurationWeeklyAverage = { PA: 0, APA: 0 };
    let contractedActivityCategories = [];

    // loop through all activities

    jobPlan.activities.forEach((activity) => {
        let durationAveragePW = 0;
        let annualOccurrences = activity["annualOccurrences"] && activity["annualOccurrences"] > 0 ? activity["annualOccurrences"] : 1;

        // if missing from the returned data - start
        if (!activity["id"]) activity["id"] = uuidv4();

        // if missing from the returned data
        if (!activity["type"]) {
            activity["type"] = {
                name: "Programmed Activity",
                code: "PA",
                contracted: true,
            };
        }

        // if missing from the returned data
        if (typeof activity["category"] === "string") {
            activity["category"] = {
                id: uuidv4(),
                code: activity["categoryType"] || "X",
                name: activity["category"],
            };
        }

        // if missing from the returned data
        if (!activity["duration"]) activity["duration"] = 0;
        if (!activity["durationUnit"]) activity["durationUnit"] = durationUnit;
        if (!activity["durationPremium"]) {
            activity["durationPremium"] = activity["paPremium"] ? activity["paPremium"] : 0;
        }
        if (!activity["durationNormal"]) {
            activity["durationNormal"] = activity["paNormal"] ? activity["paNormal"] : 0;
        }
        if (activity["durationNormal"] + activity["durationPremium"] === 0) {
            activity["durationNormal"] = activity["duration"];
        }

        // if missing from the returned data - end

        if (activity["scheduled"]) {
            if (activity["scheduled"]["dayOfWeek"]) {
                annualOccurrences = jobPlan.workingWeeks;
                durationAveragePW = (activity["durationNormal"] + activity["durationPremium"]) * (annualOccurrences / jobPlan.workingWeeks);
            }
        }

        if (activity["type"]["contracted"]) {
            if (activity["type"]["code"] === "PA") {
                contractedDurationWeeklyAverage.PA += durationAveragePW;
            } else if (activity["type"]["code"] === "APA") {
                contractedDurationWeeklyAverage.APA += durationAveragePW;
            }

            if (contractedActivityCategories[activity["category"]["name"]]) {
                contractedActivityCategories[activity["category"]["name"]] += durationAveragePW;
            } else {
                contractedActivityCategories[activity["category"]["name"]] = durationAveragePW;
            }
        }
    });

    return (
        <Card className="card">
            <CardHeader
                disableTypography={true}
                avatar={<JPAvatar type="jobplan" />}
                action={
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "8px" }}
                    >
                        <Button
                            variant="contained"
                            color="neutral"
                            disableElevation
                            component={Link}
                            to={`/jp/jobplans/${jobPlan.id}/insights`}
                            startIcon={<InsightsIcon/>}
                        >
                            Insights
                        </Button>
                        <JobPlanButton
                            id={jobPlan?.id}
                            status={jobPlan?.status}
                        />
                    </Stack>
                }
                title={
                    <Typography component="h3" className="card-header-title">
                        {title}
                    </Typography>
                }
                subheader={
                    <Typography
                        component="span"
                        className="card-header-subtitle"
                    >
                        <RenderDate date={effectiveDate} />
                    </Typography>
                }
            />
            <CardContent>
                <Box className="card-content">
                    <JobPlanDetails
                        person={jobPlan?.worker}
                        areaOfWork={jobPlan?.speciality?.name}
                        effectiveDate={jobPlan?.effectiveDate}
                        activityCount={activityCount}
                        objectiveCount={objectiveCount}
                        mainPlaceOfWork={jobPlan?.mainPlaceOfWork}
                        contractType={jobPlan?.contractType}
                        staffGroup={jobPlan?.staffGroup}
                        workingWeeks={jobPlan?.workingWeeks}
                        contractedHours={jobPlan?.contractedHours}
                        contractedDurationWeeklyAverage={
                            contractedDurationWeeklyAverage
                        }
                    />
                </Box>
            </CardContent>

            {isCompleted && (
                <CardContent>
                    <Box className="card-content">
                        <AlertMessage
                            severity="success"
                            message="This job plan has been signed off"
                        />
                    </Box>
                </CardContent>
            )}

            <CardContent>
                <JobPlanActivitySummary
                    activities={jobPlan.activities}
                    contractedHours={jobPlan.contractedHours}
                />
            </CardContent>
        </Card>
    );
}

export default JobPlanListItem;

JobPlanListItem.defaultProps = {
    jobPlan: {
        id: 0,
        status: "",
        resourceType: "JobPlan",
        worker: {
            uuid: "",
            title: "",
            initials: "",
            firstName: "",
            lastName: "",
        },
        effectiveDate: "",
        operationalDate: "",
        workingWeeks: 42,
        jobTitle: "",
        speciality: {
            id: "0",
            name: "",
        },
        organisation: {
            id: "0",
            name: "",
        },
        mainPlaceOfWork: "",
        contractType: "",
        contractedHours: "",
        staffGroup: "",
        title: "",
        notes: "",
        activities: [],
    },
};
